import React, { useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import FrequencySelector from '../../maintenance/sheets/FrequencySelector';
import { DURATION_FORM_REVEAL_OPEN_MS, EASE_OUT } from '@/components/ui/motion.ts';
import { TaskScheduleController } from './useTaskComposer.ts';

const prefersReducedMotion = () =>
	typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface ComposerFrequencyRevealProps {
	task: TaskScheduleController;
}

/**
 * The "repeat every N interval" controls, revealed as a row beneath the composer's due/repeat bar
 * when recurrence is switched on. Uses a grid-rows 0fr to 1fr transition so it eases open/closed
 * without measuring.
 */
const ComposerFrequencyReveal: React.FC<ComposerFrequencyRevealProps> = (props) => {
	const { task } = props;
	const [isSettledOpen, setIsSettledOpen] = useState(task.isRecurring);

	useEffect(() => {
		// Clipping on close is handled by deriving overflow from isRecurring below, so it takes
		// effect in the render that starts the collapse rather than a frame later. This only keeps
		// the flag honest for the next open.
		if (!task.isRecurring) {
			setIsSettledOpen(false);
			return;
		}
		// Motion-reduced, the row snaps open and no transitionend ever fires, so settle here.
		if (prefersReducedMotion()) setIsSettledOpen(true);
	}, [task.isRecurring]);

	const handleTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
		if (event.target !== event.currentTarget || event.propertyName !== 'grid-template-rows') return;
		setIsSettledOpen(task.isRecurring);
	};

	return (
		<Box
			display="grid"
			style={{ gridTemplateRows: task.isRecurring ? '1fr' : '0fr' }}
			css={{
				transition: `grid-template-rows ${DURATION_FORM_REVEAL_OPEN_MS}ms ${EASE_OUT}`,
				'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
			}}
			onTransitionEnd={handleTransitionEnd}
		>
			{/* Clipped while closed or moving, unclipped once open, so the selects' shadow is not cut
			    at the edge. Kept as an overflow toggle at the endpoints rather than folded into the
			    motion: the animation stays a transition, which survives a rapid re-toggle where
			    keyframes would restart (see .junie/skills/animation). clip-path is not an option
			    either — the dropdowns position "fixed", which escapes an overflow clip but not a
			    clip-path, so that cuts them off entirely. */}
			<Box overflow={task.isRecurring && isSettledOpen ? 'visible' : 'hidden'} minH={0}>
				<Box data-composer-frequency-content pt={2} pb={0}>
					<FrequencySelector
						value={task.frequency}
						onChange={task.setFrequency}
						isRecurring={task.isRecurring}
						onRecurringChange={task.setIsRecurring}
						showToggle={false}
						inline
					/>
				</Box>
			</Box>
		</Box>
	);
};

export default ComposerFrequencyReveal;
